import React from 'react';
import { FormattedMessage } from 'react-intl';

import { FlexRow } from 'ui-components/components/flex';

import { NewFeatureBadge } from './components/new-feature-badge';
import messages from './messages';
import style from './settings-page.module.less';
import { UserPermissionService } from '../../services/user-permission-service';

const returnTrue = () => true;
const returnFalse = () => false;

export const pages = {
  // pages explicitly in leftnav
  myProfile: {
    id: 'myprofile',
    location: 'myprofile',
    label: <FormattedMessage {...messages.myProfile} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (_, featureMap) => !featureMap?.enableSecurityPage, // replaced by pages.signInAndSecurity
  },
  signInAndSecurity: {
    id: 'signInAndSecurity',
    location: 'signin-security',
    label: <FormattedMessage {...messages.signInAndSecurity} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (_, featureMap) => featureMap?.enableSecurityPage, // replaces pages.myProfile
  },
  displayOptions: {
    id: 'displayoptions',
    location: 'displayoptions',
    label: <FormattedMessage {...messages.displayOptions} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  paymentAndSubscription: {
    id: 'payments',
    location: 'payments',
    label: <FormattedMessage {...messages.paymentAndSubscription} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasAccountManagementRole(userRoles),
  },
  users: {
    id: 'users',
    location: 'users',
    label: <FormattedMessage {...messages.userManagement} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasAccountManagementRole(userRoles),
  },
  api: {
    id: 'api',
    location: 'api',
    label: <FormattedMessage {...messages.api} />,
    getIsFeatureGated: (featureMap) => !featureMap?.enableApi,
    getIsVisible: (userRoles) => UserPermissionService.userHasConfigurationRole(userRoles),
  },
  senderEmails: {
    id: 'senderemails',
    location: 'senderemails',
    label: <FormattedMessage {...messages.senderEmails} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) => UserPermissionService.userHasConfigurationRole(userRoles) && featureMap?.enableSenderEmails,
  },
  workflow: {
    id: 'workflow',
    location: 'workflow',
    label: <FormattedMessage {...messages.workflow} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  clients3PL: {
    id: 'clients',
    location: 'clients',
    label: <FormattedMessage {...messages.clients3PL} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) => UserPermissionService.userHasConfigurationRole(userRoles) && featureMap?.enableClients,
  },
  stores: {
    id: 'stores',
    location: 'stores',
    label: <FormattedMessage {...messages.storeSetup} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasConfigurationRole(userRoles),
  },
  selectedStore: {
    id: 'selectedStore',
    location: 'stores',
    label: <FormattedMessage {...messages.selectedStoreSetup} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnFalse,
  },
  filters: {
    id: 'filters',
    location: 'filters',
    label: <FormattedMessage {...messages.filters} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasConfigurationRole(userRoles),
  },
  smartRoutingRules: {
    id: 'smartroutingrules',
    location: 'smartroutingrules',
    label: <FormattedMessage {...messages.smartRoutingRules} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) => UserPermissionService.userHasConfigurationRole(userRoles) && featureMap.enableSmartRouting,
  },
  automationRules: {
    id: 'automationrules',
    location: 'automationrules',
    label: <FormattedMessage {...messages.automationRules} />,
    getIsFeatureGated: (featureMap) => !featureMap?.enableAutomationRules,
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasConfigurationRole(userRoles) && !featureMap?.enableAutomationWorkflows,
  },
  automationRulesWithWorkflowsBadge: {
    id: 'automationrules',
    location: 'automationrules',
    label: (
      <FlexRow className={style['nav-item-with-badge']}>
        <FormattedMessage {...messages.automationRules} />
        <NewFeatureBadge />
      </FlexRow>
    ),
    getIsFeatureGated: (featureMap) => !featureMap?.enableAutomationRules,
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasConfigurationRole(userRoles) && !!featureMap?.enableAutomationWorkflows,
  },
  lossPrevention: {
    id: 'lossprevention',
    location: 'loss-prevention',
    label: <FormattedMessage {...messages.lossPrevention} />,
    getIsFeatureGated: (featureMap) => !featureMap?.enableLossPrevention,
    getIsVisible: (userRoles, featureMap) => UserPermissionService.userHasConfigurationRole(userRoles) && featureMap?.enableLossPrevention,
  },
  smartFillRules: {
    id: 'smartfillrules',
    location: 'smartfillrules',
    label: <FormattedMessage {...messages.smartfill} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasConfigurationRole(userRoles) && (featureMap?.enableSmartFill || featureMap?.isSmartFillABTestEnabled),
  },
  warehouses: {
    id: 'warehouses',
    location: 'warehouses',
    label: <FormattedMessage {...messages.warehouses} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasConfigurationRole(userRoles),
  },
  carriers: {
    id: 'carriers',
    location: 'carriers',
    label: <FormattedMessage {...messages.shippingProviders} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasConfigurationRole(userRoles),
  },
  fulfillmentProviders: {
    id: 'fulfillmentProviders',
    location: 'fulfillment',
    label: <FormattedMessage {...messages.fulfillmentProviders} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasConfigurationRole(userRoles),
  },
  freightProviders: {
    id: 'freightProviders',
    location: 'freightproviders',
    label: <FormattedMessage {...messages.freightProviders} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) => UserPermissionService.userHasConfigurationRole(userRoles) && featureMap?.enableLtlFreight,
  },
  insurance: {
    id: 'insurance',
    location: 'insurance',
    label: <FormattedMessage {...messages.insurance} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) => UserPermissionService.userHasConfigurationRole(userRoles) && featureMap?.enableInsurance,
  },
  packages: {
    id: 'packages',
    location: 'packages',
    label: <FormattedMessage {...messages.packages} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasConfigurationRole(userRoles),
  },
  returns: {
    id: 'returns',
    location: 'returns',
    label: <FormattedMessage {...messages.returns} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasConfigurationRole(userRoles),
  },
  international: {
    id: 'international',
    location: 'international',
    label: <FormattedMessage {...messages.international} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasConfigurationRole(userRoles),
  },
  printing: {
    id: 'printing',
    location: 'printing',
    label: <FormattedMessage {...messages.printingSetup} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  connect: {
    id: 'connect',
    location: 'connect',
    label: <FormattedMessage {...messages.connect} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  emailTemplates: {
    id: 'emailtemplates',
    location: 'emailtemplates',
    label: <FormattedMessage {...messages.emailTemplates} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, { hasCustomEmailTemplates }) =>
      UserPermissionService.userHasConfigurationRole(userRoles) && hasCustomEmailTemplates,
  },
  packingSlips: {
    id: 'packingslips',
    location: 'packingslips',
    label: <FormattedMessage {...messages.packingSlips} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, { hasCustomPackingSlipsFeature }) =>
      UserPermissionService.userHasConfigurationRole(userRoles) && hasCustomPackingSlipsFeature,
  },
  pickList: {
    id: 'picklist',
    location: 'picklist',
    label: <FormattedMessage {...messages.pickList} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasConfigurationRole(userRoles) && featureMap?.enablePicklistsCustomized,
  },
  inventorySettings: {
    id: 'inventorysettings',
    location: 'inventorysettings',
    label: <FormattedMessage {...messages.inventorySettings} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasInventoryManagementRole(userRoles) && featureMap?.enableInventoryManagementBasicFeatureGate,
  },
  allocationStrategy: {
    id: 'allocationstrategy',
    location: 'allocationstrategy',
    label: <FormattedMessage {...messages.allocationStrategy} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasInventoryManagementRole(userRoles) && featureMap?.enableInventoryManagementBasicFeatureGate,
  },
  inventoryWarehouses: {
    id: 'inventorywarehouses',
    location: 'inventorywarehouses',
    label: <FormattedMessage {...messages.inventoryWarehouses} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasInventoryManagementRole(userRoles) && featureMap?.enableInventoryManagementBasicFeatureGate,
  },
  inventorySources: {
    id: 'inventorysources',
    location: 'inventorysources',
    label: <FormattedMessage {...messages.inventorySources} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasInventoryManagementRole(userRoles) &&
      !featureMap?.enableInventorySyncPage &&
      featureMap?.enableInventoryManagementBasicFeatureGate,
  },
  inventorySync: {
    id: 'inventorysync',
    location: 'inventorysync',
    label: (
      <FlexRow>
        <FormattedMessage {...messages.inventorysync} />
      </FlexRow>
    ),
    getIsFeatureGated: returnFalse,
    shouldUseLabelInBreadCrumb: false,
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasInventoryManagementRole(userRoles) &&
      featureMap?.enableInventorySyncPage &&
      featureMap?.enableInventoryManagementBasicFeatureGate,
  },
  integrations: {
    id: 'integrations',
    location: 'integrations',
    label: <FormattedMessage {...messages.integrationPartners} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasConfigurationRole(userRoles),
  },
  addOnMarketplace: {
    id: 'addonmarketplace',
    location: 'addonmarketplace',
    label: <FormattedMessage {...messages.addOnMarketplace} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasConfigurationRole(userRoles),
  },
  trackingPages: {
    id: 'trackingpages',
    location: 'trackingpages',
    label: <FormattedMessage {...messages.trackingPages} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles) => UserPermissionService.userHasConfigurationRole(userRoles), // V2 checks against a setting that is true for all but 4 sellers
  },
  inventoryStrategy: {
    id: 'inventoryStrategy',
    location: 'allocationstrategy',
    label: <FormattedMessage {...messages.inventoryStrategy} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  }, // this name will eventually change to rateShopper
  serviceCollections: {
    id: 'rateShopper',
    location: 'rateShopper',
    label: <FormattedMessage {...messages.rateShopper} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) => featureMap?.enableRateShopper,
  },
  warehousePicking: {
    id: 'warehousepicking',
    location: 'warehousepicking',
    label: <FormattedMessage {...messages.warehousePicking} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasConfigurationRole(userRoles) && featureMap?.enablePickPackSettings,
  },
  warehousePacking: {
    id: 'warehousepacking',
    location: 'warehousepacking',
    label: <FormattedMessage {...messages.warehousePacking} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasConfigurationRole(userRoles) && featureMap?.enablePickPackSettings,
  },
  // pages not in leftnav
  settings: {
    id: 'settings',
    location: 'settings',
    label: <FormattedMessage {...messages.settings} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  mailChimp: {
    id: 'mailchimp',
    location: 'MailChimp',
    label: <FormattedMessage {...messages.mailChimp} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  constantContact: {
    id: 'constantContact',
    location: 'ConstantContact',
    label: <FormattedMessage {...messages.constantContact} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  eDesk: {
    id: 'eDesk',
    location: 'Edesk',
    label: <FormattedMessage {...messages.eDesk} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  quickbooksOnline: {
    id: 'quickbooksOnline',
    location: 'QuickbooksOnline',
    label: <FormattedMessage {...messages.quickbooksOnline} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  zapier: {
    id: 'zapier',
    location: 'Zapier',
    label: <FormattedMessage {...messages.zapier} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  returnRabbit: {
    id: 'returnrabbit',
    location: 'ReturnRabbit',
    label: <FormattedMessage {...messages.returnRabbit} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  gorgias: {
    id: 'gorgias',
    location: 'Gorgias',
    label: <FormattedMessage {...messages.gorgias} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  webhooks: {
    id: 'webhooks',
    location: 'Webhooks',
    label: <FormattedMessage {...messages.webhooks} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  shipperHQ: {
    id: 'shipperHQ',
    location: 'ShipperHQ',
    label: <FormattedMessage {...messages.shipperHQ} />,
    getIsFeatureGated: returnFalse,
  },
  google: {
    id: 'google',
    location: 'Google',
    label: <FormattedMessage {...messages.google} />,
    getIsFeatureGated: returnFalse,
  },
  amazon: {
    id: 'amazon',
    location: 'Amazon',
    label: <FormattedMessage {...messages.amazon} />,
    getIsFeatureGated: returnFalse,
  },
  invoices: {
    id: 'invoices',
    location: 'invoices',
    label: <FormattedMessage {...messages.invoices} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  archive: {
    id: 'archive',
    location: 'archive',
    label: <FormattedMessage {...messages.archive} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: returnTrue,
  },
  emailVerification: {
    id: 'emailVerification',
    location: 'verify',
    label: <FormattedMessage {...messages.emailVerification} />,
    getIsFeatureGated: returnFalse,
  },
  pickupSettings: {
    id: 'pickupSettings',
    location: 'pickup-settings',
    label: <FormattedMessage {...messages.pickupSettings} />,
    getIsFeatureGated: returnFalse,
  },
  cancelStamps: {
    id: 'cancelStamps',
    location: 'cancel-stamps',
    label: <FormattedMessage {...messages.cancelStamps} />,
    getIsFeatureGated: returnFalse,
  },
  unishippers: {
    id: 'unishippers',
    location: 'unishippers',
    label: <FormattedMessage {...messages.unishippers} />,
    getIsFeatureGated: returnFalse,
  },
  additionalCarriers: {
    id: 'additionalCarriers',
    location: 'additionalCarriers',
    label: <FormattedMessage {...messages.unishippers} />,
    getIsFeatureGated: returnFalse,
  },
  personalShipsurance: {
    id: 'personalShipsurance',
    location: 'shipsurance',
    label: <FormattedMessage {...messages.shipsurance} />,
    getIsFeatureGated: returnFalse,
  },
  onboard: {
    id: 'onboard',
    location: 'onboard',
    shouldRenderAnIcon: false,
    getCustomIcon: () => null,
    label: '',
    getIsFeatureGated: returnFalse,
  },
  checkoutRates: {
    id: 'checkoutrates',
    location: 'checkoutrates',
    label: <FormattedMessage {...messages.checkoutRates} />,
    getIsFeatureGated: returnFalse,
  },
  labs: {
    id: 'labs',
    location: 'labs',
    label: <FormattedMessage {...messages.labs} />,
    getIsFeatureGated: returnFalse,
    getIsVisible: (_, featureMap) => featureMap?.enableLabsSettingsPage,
  },
  refundAssist: {
    id: 'refundassist',
    location: 'refundassist',
    label: (
      <FlexRow className={style['nav-item-with-badge']}>
        <FormattedMessage {...messages.refundAssist} />
      </FlexRow>
    ),
    getIsFeatureGated: returnFalse,
    getIsVisible: (_, featureMap) => featureMap?.isRefundAssistVisible,
  },
  refundAssistWithNewBadge: {
    id: 'refundassist',
    location: 'refundassist',
    label: (
      <FlexRow className={style['nav-item-with-badge']}>
        <FormattedMessage {...messages.refundAssist} />
        <NewFeatureBadge />
      </FlexRow>
    ),
    getIsFeatureGated: returnFalse,
    getIsVisible: (_, featureMap) => featureMap?.isRefundAssistVisible,
  },
};

const navItems = [
  {
    id: 'account',
    location: 'account',
    label: <FormattedMessage {...messages.account} />,
    nonNavigable: true,
    items: [
      pages.myProfile,
      pages.signInAndSecurity,
      pages.displayOptions,
      pages.paymentAndSubscription,
      pages.users,
      pages.api,
      pages.senderEmails,
    ],
  },
  {
    id: 'sellingchannels',
    location: 'sellingchannels',
    label: <FormattedMessage {...messages.sellingChannels} />,
    nonNavigable: true,
    items: [pages.stores, pages.clients3PL],
  },
  {
    id: 'brandedtracking',
    location: 'brandedtracking',
    label: <FormattedMessage {...messages.brandedTracking} />,
    nonNavigable: true,
    items: [pages.trackingPages],
  },
  {
    id: 'templates',
    location: 'templates',
    label: <FormattedMessage {...messages.templates} />,
    nonNavigable: true,
    items: [pages.emailTemplates, pages.packingSlips, pages.pickList],
  },
  {
    id: 'automation',
    location: 'automation',
    label: <FormattedMessage {...messages.automation} />,
    nonNavigable: true,
    items: [
      pages.smartRoutingRules,
      pages.filters,
      pages.automationRules,
      pages.automationRulesWithWorkflowsBadge,
      pages.lossPrevention,
      pages.smartFillRules,
    ],
  },
  {
    id: 'shipping',
    location: 'shipping',
    label: <FormattedMessage {...messages.shipping} />,
    nonNavigable: true,
    items: [
      pages.carriers,
      pages.insurance,
      pages.fulfillmentProviders,
      pages.freightProviders,
      pages.serviceCollections,
      pages.workflow,
      pages.international,
      pages.packages,
      pages.warehouses,
      pages.returns,
      pages.refundAssistWithNewBadge,
    ],
  },
  {
    id: 'printingmenu',
    location: 'printingmenu',
    label: <FormattedMessage {...messages.printing} />,
    nonNavigable: true,
    items: [pages.printing, pages.connect],
  },
  {
    id: 'inventory',
    location: 'inventory',
    label: <FormattedMessage {...messages.inventory} />,
    nonNavigable: true,
    items: [pages.inventorySettings, pages.allocationStrategy, pages.inventoryWarehouses, pages.inventorySources, pages.inventorySync],
  },
  {
    id: 'warehouse',
    location: 'warehouse',
    label: <FormattedMessage {...messages.warehouse} />,
    nonNavigable: true,
    items: [pages.warehousePicking, pages.warehousePacking],
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasConfigurationRole(userRoles) && featureMap?.enablePickPackSettings,
  },
  {
    id: 'addonmarketplace',
    location: 'addonmarketplace',
    label: (
      <div className={style['add-ons-nav-item-label']}>
        <FormattedMessage {...messages.addOnMarketplace} />
      </div>
    ),
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasAccountManagementRole(userRoles) && featureMap?.enableAddOnCentral,
  },
  {
    id: 'integrationmenu',
    location: 'integrations',
    label: <FormattedMessage {...messages.integrationPartners} />,
    getIsVisible: (userRoles, featureMap) =>
      UserPermissionService.userHasConfigurationRole(userRoles) && featureMap?.enableIntegrationPartners,
  },
  {
    id: 'labs',
    location: 'labs',
    label: (
      <FlexRow>
        <FormattedMessage {...messages.labs} />
      </FlexRow>
    ),
    getIsVisible: (_, featureMap) => featureMap?.enableLabsSettingsPage,
  },
];

export const findPageLabelFromLocation = (location) => {
  const matchId = Object.keys(pages).find((p) => pages[p].location === location);

  const { label, shouldUseLabelInBreadCrumb = true } = matchId ? pages[matchId] : {};

  if (!matchId || !shouldUseLabelInBreadCrumb) {
    return messages[location] ? <FormattedMessage {...messages[location]} /> : null;
  }

  return label;
};

export default navItems;
